import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { toast } from "react-toastify";

import type { StepperContextValue } from "@/context";
import { loginService } from "@/services/login";
import type { ApiEnvelope } from "@/types/api";
import { getT } from "@/utils/helpers";

export interface UserInfoInput {
  userName?: string;
  password?: string;
  [key: string]: unknown;
}

export interface UserInfo extends UserInfoInput {
  systemUserId?: string | number;
  phoneNumber?: string;
}

interface UserInfoError {
  message?: string;
  [key: string]: unknown;
}

interface RejectedUserInfoResponse {
  data: ApiEnvelope<UserInfoError>;
}

interface UserInfoStore {
  data: UserInfo;
  error: unknown;
  userInfo: (
    userInfoData: UserInfoInput,
    stepper: Pick<StepperContextValue, "moveNext">
  ) => Promise<void>;
}

export const useUserInfoStore = create<UserInfoStore>()(
  immer((set) => ({
    data: {},
    error: null,
    userInfo: async (userInfoData, stepper) => {
      set({ data: {}, error: null });
      const t = await getT("login");

      try {
        const response = await loginService.userInfo<ApiEnvelope<UserInfo>>(userInfoData);
        const { data } = response;

        if (data?.header?.isSuccess) {
          stepper.moveNext();
          set({
            data: {
              ...userInfoData,
              ...data.body,
            },
          });

          await loginService.sendOtp({
            systemUserId: data.body.systemUserId,
            otpType: 1,
            otpChannel: 1,
          });
        } else {
          set({ error: data.body });
          toast.error(t("loginError") as string);
        }
      } catch (error) {
        const rejectedResponse = error as RejectedUserInfoResponse;
        set({ error: rejectedResponse.data.body });
        toast.error(rejectedResponse.data.body.message);
      }
    },
  }))
);
